import type { SxProps, Theme } from "@mui/material/styles";

import EmailDesignPreview from "containers/admin/clients/touchpoint/components/email-design-preview/email-design-preview";
import LPDesignPreview from "containers/admin/clients/touchpoint/components/lp-design-preview/lp-design-preview";
import { SkeletonLoader } from "components/modals/create-parent-touchpoint-modal/components/skeleton-loader";

import { VersionPreviewData } from "components/modals/compare-versions-modal/hooks/use-compare-versions";

import { Box } from "ui/box";
import { Typography } from "ui/typography";

import Icons from "assets";

const REWRITER_CHANGES_SX: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  gap: 0.5,
  mt: 2,
};

const REWRITER_CHANGES_LABEL_SX: SxProps<Theme> = (theme) => ({
  fontWeight: 600,
  color: theme.palette.grey[900],
});

const REWRITER_CHANGES_TEXT_SX: SxProps<Theme> = (theme) => ({
  color: theme.palette.grey[900],
});

interface VersionCompareColumnProps {
  isLoading: boolean;
  hasVersion: boolean;
  showError: boolean;
  version?: VersionPreviewData;
  isEmailType: boolean;
  side: "left" | "right";
  hideSubjectAndPreheader?: boolean;
}

const VersionCompareColumn = ({
  isLoading,
  hasVersion,
  showError,
  version,
  isEmailType,
  side,
  hideSubjectAndPreheader = false,
}: VersionCompareColumnProps) => {
  return (
    <div className="flex-1 min-w-0 max-w-[400px] relative">
      {isLoading && (
        <div className="flex items-center justify-center w-full min-h-full absolute inset-0">
          <SkeletonLoader />
        </div>
      )}
      {hasVersion && (
        <div className="shadow shadow-gray-100 relative">
          {isEmailType ? (
            <>
              {!hideSubjectAndPreheader && (
                <div className="bg-gray-100 border border-b-0 border-gray-200 px-4 py-3">
                  <p className="font-medium text-gray-600 text-sm">
                    {version?.subjectLine || "[Email subject line]"}
                  </p>
                  <p className="text-gray-500 text-xs mt-1">
                    {version?.preheader || "[Email preheader text...]"}
                  </p>
                </div>
              )}
              <EmailDesignPreview
                htmlContent={version!.html!}
                htmlJSON={version!.beeJson!}
                isCommentMode={false}
                mobileView={true}
              />
            </>
          ) : (
            <LPDesignPreview
              htmlContent={version!.html!}
              htmlJSON={version!.beeJson!}
              isCommentMode={false}
              mobileView={true}
              instanceId={side}
            />
          )}
        </div>
      )}
      {hasVersion && version?.rewriterChanges && (
        <Box sx={REWRITER_CHANGES_SX}>
          <Typography variant="body2" sx={REWRITER_CHANGES_LABEL_SX}>
            Rewriter changes:
          </Typography>
          <Typography variant="body2" sx={REWRITER_CHANGES_TEXT_SX}>
            {version.rewriterChanges}
          </Typography>
        </Box>
      )}
      {showError && (
        <div className="h-full min-h-[400px] relative border border-gray-200 shadow shadow-gray-100 bg-gray-50 p-6">
          <div className="sticky top-1/2 -translate-y-1/2 flex flex-col items-center justify-center text-center p-4">
            <div className="flex items-center gap-2 mb-4 text-critical-700">
              <Icons.ExclamationCircle />
              <h3 className="font-medium">Failed to load version</h3>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VersionCompareColumn;
